import { execSync, spawn, ChildProcess } from 'child_process';
import { existsSync, readFileSync, writeFileSync } from 'fs';
import { tmpdir } from 'os';
import { join, dirname } from 'path';
import { fileURLToPath } from 'url';
import { parse } from '@iarna/toml';
import chalk from 'chalk';
import { isNoResumeCliOptionEnabled } from '../../lib/cloister/no-resume-mode.js';
import { writeDevSupervisorMarker, clearDevSupervisorMarker } from '../../lib/dev-supervisor.js';
import { getInternalTokenSync } from '../../lib/internal-token.js';

const __dirname = dirname(fileURLToPath(import.meta.url));

function ensureDashboardBundle(): boolean {
  const bundledServer = join(__dirname, '..', 'dashboard', 'server.js');
  const srcDashboard = join(__dirname, '..', '..', 'src', 'dashboard');

  if (existsSync(bundledServer)) {
    return true;
  }

  if (!existsSync(srcDashboard)) {
    return false;
  }

  console.log(chalk.yellow('⚠ Dashboard server bundle missing; rebuilding...'));
  try {
    execSync('npm run build:dashboard:server', {
      cwd: join(__dirname, '..', '..'),
      stdio: ['pipe', 'inherit', 'pipe'],
    });
  } catch {
    return false;
  }

  return existsSync(bundledServer);
}

function resolveNode22(): string {
  const nvmNode = '/home/eltmon/.config/nvm/versions/node/v22.22.0/bin/node';
  if (existsSync(nvmNode)) return nvmNode;
  return 'node';
}

function readConfig() {
  const configFile = join(process.env.HOME || '', '.overdeck', 'config.toml');
  const defaults = {
    traefikEnabled: false,
    traefikDomain: 'overdeck.localhost',
    dashboardPort: 3010,
    dashboardApiPort: 3011,
  };

  if (!existsSync(configFile)) return defaults;

  try {
    const config = parse(readFileSync(configFile, 'utf-8')) as any;
    return {
      traefikEnabled: config.traefik?.enabled === true,
      traefikDomain: config.traefik?.domain || defaults.traefikDomain,
      dashboardPort: config.dashboard?.port || defaults.dashboardPort,
      dashboardApiPort: config.dashboard?.api_port || defaults.dashboardApiPort,
    };
  } catch {
    return defaults;
  }
}

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitForHealth(port: number, path: string, timeoutMs = 15000): Promise<void> {
  const deadline = Date.now() + timeoutMs;
  const url = `http://127.0.0.1:${port}${path}`;
  while (Date.now() < deadline) {
    try {
      const res = await fetch(url, { signal: AbortSignal.timeout(2000) });
      if (res.ok) return;
    } catch {
      // ignore
    }
    await sleep(250);
  }
  throw new Error(`Health check at ${url} did not pass within ${timeoutMs}ms`);
}

async function waitForHttp200(port: number, timeoutMs = 10000): Promise<void> {
  const deadline = Date.now() + timeoutMs;
  const url = `http://127.0.0.1:${port}`;
  while (Date.now() < deadline) {
    try {
      const res = await fetch(url, { signal: AbortSignal.timeout(2000) });
      if (res.status === 200) return;
    } catch {
      // ignore
    }
    await sleep(500);
  }
  throw new Error(`Frontend at ${url} did not return 200 within ${timeoutMs}ms`);
}

function killPort(port: number, signal: 'TERM' | 'KILL' = 'TERM'): void {
  try {
    execSync(`fuser -k -${signal} ${port}/tcp 2>/dev/null || true`, { stdio: 'pipe' });
  } catch {
    // ignore
  }
}

/**
 * Kill ALL bundled dashboard-server processes by path — not just the current port
 * owner. An orphan that already lost the port (e.g. after a failed restart) is
 * invisible to killPort(), survives forever, and can run a second Deacon racing
 * the live one. PAN-1622.
 *
 * Host-side only (PAN-1763): workspace/UAT stack `server` containers run the same
 * `node dist/dashboard/server.js` cmdline and are visible to host pkill, so a bare
 * pattern kill SIGTERMs every stack's server container, flipping stacks unhealthy.
 * Skip any PID whose cgroup shows it lives inside a container.
 */
function killAllDashboardServers(signal: 'TERM' | 'KILL' = 'TERM'): void {
  let pids = '';
  try {
    pids = execSync(`pgrep -f 'dist/dashboard/server\\.js' 2>/dev/null || true`, {
      stdio: 'pipe',
    }).toString();
  } catch {
    return;
  }
  for (const line of pids.split('\n')) {
    const pid = Number(line.trim());
    if (!pid || pid === process.pid) continue;
    let cgroup = '';
    try {
      cgroup = readFileSync(`/proc/${pid}/cgroup`, 'utf-8');
    } catch {
      continue; // process already gone
    }
    if (/docker|containerd|libpod/.test(cgroup)) continue; // in-container peer, not ours
    try {
      process.kill(pid, signal === 'KILL' ? 'SIGKILL' : 'SIGTERM');
    } catch {
      // ignore
    }
  }
}

function waitForPortFree(port: number, timeoutMs = 5000): Promise<void> {
  return new Promise((resolve, reject) => {
    const deadline = Date.now() + timeoutMs;
    const check = () => {
      try {
        execSync(`bash -c 'echo >/dev/tcp/127.0.0.1/${port}'`, { stdio: 'pipe', timeout: 500 });
      } catch {
        resolve();
        return;
      }
      if (Date.now() >= deadline) {
        reject(new Error(`Port ${port} did not free within ${timeoutMs}ms`));
        return;
      }
      setTimeout(check, 100);
    };
    check();
  });
}

async function startSidecars(): Promise<void> {
  // CLIProxy
  try {
    const { startCliproxySync, CLIPROXY_PORT } = await import('../../lib/cliproxy.js');
    console.log(chalk.dim('Starting CLIProxyAPI sidecar (GPT subscription router)...'));
    startCliproxySync();
    console.log(chalk.green(`✓ CLIProxyAPI listening on http://127.0.0.1:${CLIPROXY_PORT}`));
  } catch (error: any) {
    console.log(chalk.yellow('⚠ Failed to start CLIProxyAPI sidecar:'), error?.message || String(error));
  }

  // smee
  try {
    const { startSmeeProcessSync } = await import('../../lib/smee.js');
    console.log(chalk.dim('\nStarting smee-client webhook relay...'));
    startSmeeProcessSync();
  } catch (error: any) {
    console.log(chalk.yellow('⚠ Failed to start smee-client:'), error?.message || String(error));
  }

  // TLDR
  try {
    const { getTldrDaemonServiceSync } = await import('../../lib/tldr-daemon.js');
    const projectRoot = process.cwd();
    const venvPath = join(projectRoot, '.venv');
    if (existsSync(venvPath)) {
      console.log(chalk.dim('\nStarting TLDR daemon for project root...'));
      const tldrService = getTldrDaemonServiceSync(projectRoot, venvPath);
      await tldrService.start(true);
      console.log(chalk.green('✓ TLDR daemon started'));
    } else {
      console.log(chalk.dim('\nSkipping TLDR daemon (no .venv found)'));
    }
  } catch (error: any) {
    console.log(chalk.yellow('⚠ Failed to start TLDR daemon:'), error?.message || String(error));
  }

  // Supervisor
  try {
    const { startSupervisorProcessSync, getSupervisorPortSync } = await import('../../lib/supervisor.js');
    startSupervisorProcessSync();
    console.log(chalk.green(`✓ Supervisor listening on http://127.0.0.1:${getSupervisorPortSync()}`));
  } catch (error: any) {
    console.log(chalk.yellow('⚠ Failed to start supervisor:'), error?.message || String(error));
  }
}

export async function devCommand(options: { skipTraefik?: boolean; deacon?: boolean; noResume?: boolean; resume?: boolean }) {
  const noResume = isNoResumeCliOptionEnabled(options);

  // Force dev mode for Traefik config generation and all downstream code
  process.env['OVERDECK_DEV'] = '1';

  const config = readConfig();
  const node22 = resolveNode22();
  const bundledServer = join(__dirname, '..', 'dashboard', 'server.js');
  const frontendDir = join(__dirname, '..', '..', 'src', 'dashboard', 'frontend');
  // PAN-1670: sentinel file the Vite plugin watches; touched after a hot-reload's
  // API child is healthy again so Vite pushes a full browser reload (recovers the
  // tab instead of leaving it stuck on "Reconnecting…").
  const reloadSignalPath = join(tmpdir(), `pan-dev-fullreload-${config.dashboardPort}.signal`);

  console.log(chalk.bold('Starting Overdeck in development mode...\n'));

  // Refuse to start a second dev session — two supervisors would fight over the
  // ports, each respawning children the other just killed. readDevSupervisorMarker
  // self-heals a stale marker left by a crashed `pan dev`.
  {
    const { readDevSupervisorMarker } = await import('../../lib/dev-supervisor.js');
    const existing = readDevSupervisorMarker();
    if (existing && existing.pid !== process.pid) {
      console.error(chalk.yellow(`A \`pan dev\` session is already running (pid ${existing.pid}).`));
      console.error(chalk.dim('  Stop it first with `pan down` (or Ctrl-C in that terminal) before starting another.'));
      process.exitCode = 2;
      return;
    }
  }

  if (noResume) {
    console.log(chalk.yellow('  [no-resume mode active] Agent auto-resume is disabled for this dashboard boot'));
  }

  // ── Auto-sync ──────────────────────────────────────────────────────────────
  {
    const origWrite = process.stdout.write;
    const origErrWrite = process.stderr.write;
    try {
      const { syncCommand } = await import('./sync.js');
      process.stdout.write = () => true;
      process.stderr.write = () => true;
      await syncCommand({});
      process.stdout.write = origWrite;
      process.stderr.write = origErrWrite;
      console.log(chalk.dim('  Auto-synced skills, hooks, and MCP config'));
    } catch {
      process.stdout.write = origWrite;
      process.stderr.write = origErrWrite;
      console.log(chalk.yellow('⚠ Auto-sync failed (non-fatal, continuing startup)'));
    }
  }

  // ── Traefik ────────────────────────────────────────────────────────────────
  if (config.traefikEnabled && !options.skipTraefik) {
    try {
      const { generateOverdeckTraefikConfigSync, ensureProjectCertsSync, generateTlsConfigSync, cleanupStaleTlsSectionsSync } =
        await import('../../lib/traefik.js');

      cleanupStaleTlsSectionsSync();
      if (generateOverdeckTraefikConfigSync('dev')) {
        console.log(chalk.dim('  Regenerated Traefik config for dev mode'));
      }
      const generatedDomains = ensureProjectCertsSync();
      for (const domain of generatedDomains) {
        console.log(chalk.dim(`  Generated wildcard cert for *.${domain}`));
      }
      if (generateTlsConfigSync()) {
        console.log(chalk.dim('  Generated TLS config (tls.yml)'));
      }
    } catch {
      console.log(chalk.yellow('Warning: Could not regenerate Traefik config'));
    }

    try {
      const { ensureBaseDomain, detectDnsSyncMethod, syncDnsToWindows } = await import('../../lib/dns.js');
      const configFile = join(process.env.HOME || '', '.overdeck', 'config.toml');
      const dnsMethod =
        (existsSync(configFile) ? (parse(readFileSync(configFile, 'utf-8')) as any).traefik?.dns_sync_method : null) ||
        detectDnsSyncMethod();
      ensureBaseDomain(dnsMethod, config.traefikDomain);
      if (dnsMethod === 'wsl2hosts') {
        syncDnsToWindows().catch(() => {});
      }
    } catch {
      console.log(chalk.yellow(`Warning: Could not ensure DNS for ${config.traefikDomain}`));
    }

    const traefikDir = join(process.env.HOME || '', '.overdeck', 'traefik');
    if (existsSync(traefikDir)) {
      try {
        console.log(chalk.dim('Starting Traefik...'));
        execSync('docker compose up -d', { cwd: traefikDir, stdio: 'pipe' });
        console.log(chalk.green('✓ Traefik started'));
      } catch {
        console.log(chalk.yellow('⚠ Failed to start Traefik (continuing anyway)'));
      }
    }
  }

  // ── Ensure bundle ──────────────────────────────────────────────────────────
  if (!ensureDashboardBundle()) {
    console.error(chalk.red('Error: Could not build dashboard server bundle'));
    process.exit(1);
  }

  // ── Kill existing processes ────────────────────────────────────────────────
  console.log(chalk.dim('Cleaning up existing dashboard processes...'));
  // PAN-1622: kill ALL dashboard servers (not just the port owner) so orphans
  // can't keep serving stale data or run a second Deacon. Escalate SIGTERM →
  // SIGKILL if the ports don't free, and ABORT rather than spawn a doomed child
  // that can't bind (which silently leaves the stale server serving).
  killAllDashboardServers('TERM');
  killPort(config.dashboardPort, 'TERM');
  killPort(config.dashboardApiPort, 'TERM');
  try {
    await Promise.all([
      waitForPortFree(config.dashboardPort, 5000),
      waitForPortFree(config.dashboardApiPort, 5000),
    ]);
  } catch {
    console.log(chalk.yellow('Ports still busy after SIGTERM — escalating to SIGKILL...'));
    killAllDashboardServers('KILL');
    killPort(config.dashboardPort, 'KILL');
    killPort(config.dashboardApiPort, 'KILL');
    try {
      await Promise.all([
        waitForPortFree(config.dashboardPort, 5000),
        waitForPortFree(config.dashboardApiPort, 5000),
      ]);
    } catch {
      console.error(chalk.red(
        `Port ${config.dashboardApiPort} is still in use after SIGKILL. A stuck process ` +
        `is holding it — run \`pan down\`, or kill it manually, then retry \`pan dev\`.`,
      ));
      process.exit(1);
    }
  }

  // Tracked here (not in shutdown()) so child-close handlers can see it.
  // `shuttingDown` = pan dev is intentionally tearing down (Ctrl-C / SIGTERM /
  // routed `pan down`) → children are NOT respawned.
  // `supervising`  = initial startup succeeded → a stray child death IS respawned.
  let shuttingDown = false;
  let supervising = false;

  // Re-pointed each time a child is (re)spawned so shutdown() and the respawn
  // schedulers always reference the live process.
  let apiChild!: ChildProcess;
  let viteChild!: ChildProcess;

  // Crash-loop guards — timestamps of recent restarts per child. If a child
  // dies faster than we can keep it up, give up rather than spin forever (the
  // root cause is a real error, not a stray kill — masking it would be a bandaid).
  let apiRestarts: number[] = [];
  let viteRestarts: number[] = [];

  // PAN-1662: hot-reload coordination. `intentionalApiKill` tells the API
  // child's close handler that the next death is a deliberate reload (rebuild +
  // restart in place) — NOT a crash — so it respawns immediately without
  // counting toward the crash-loop guard. `apiReloading`/`reloadPending`
  // single-flight concurrent reload triggers.
  let intentionalApiKill = false;
  let apiReloading = false;
  let reloadPending = false;

  const tooManyRestarts = (stamps: number[]): boolean => {
    const now = Date.now();
    const recent = stamps.filter((t) => now - t < 60_000);
    stamps.length = 0;
    stamps.push(...recent, now);
    return recent.length >= 5;
  };

  // ── API server (Node 22) ─────────────────────────────────────────────────────
  const startApi = (): ChildProcess => {
    const child = spawn(node22, [bundledServer], {
      detached: false,
      stdio: 'pipe',
      env: {
        ...process.env,
        API_PORT: String(config.dashboardApiPort),
        OVERDECK_MODE: 'development',
        ...(options.deacon === false ? { OVERDECK_DISABLE_DEACON: '1' } : {}),
        ...(noResume ? { OVERDECK_NO_RESUME: '1' } : {}),
      },
    });
    child.stdout?.on('data', (data) => process.stdout.write(chalk.dim(`[server] ${data}`)));
    child.stderr?.on('data', (data) => process.stderr.write(chalk.dim(`[server] ${data}`)));
    child.on('error', (err) => {
      console.error(chalk.red('Failed to start API server:'), err.message);
      if (!supervising) process.exit(1);
      else scheduleApiRespawn();
    });
    child.on('close', (code, signal) => {
      console.error(chalk.yellow(`[pan dev] API child closed: pid=${child.pid} code=${code} signal=${signal ?? 'none'} shuttingDown=${shuttingDown}`));
      if (!supervising) {
        if (code !== 0 && code !== null) {
          console.error(chalk.red(`API server exited with code ${code}`));
          process.exit(1);
        }
        return;
      }
      if (shuttingDown) return;
      if (intentionalApiKill) {
        // Deliberate reload restart — respawn immediately with the freshly-built
        // bundle and do NOT count it as a crash (PAN-1662).
        intentionalApiKill = false;
        void (async () => {
          killPort(config.dashboardApiPort);
          try { await waitForPortFree(config.dashboardApiPort, 3000); } catch { /* proceed */ }
          if (!supervising || shuttingDown) return;
          apiChild = startApi();
        })();
        return;
      }
      scheduleApiRespawn();
    });
    return child;
  };

  const scheduleApiRespawn = (): void => {
    if (!supervising || shuttingDown) return;
    if (tooManyRestarts(apiRestarts)) {
      console.error(chalk.red('[pan dev] API server crash-looping (≥5 restarts in 60s) — giving up. Fix the underlying error, then re-run `pan dev`.'));
      void shutdown('crash-loop');
      return;
    }
    const delayMs = Math.min(500 * apiRestarts.length, 5000);
    console.error(chalk.yellow(`[pan dev] API server died unexpectedly — reclaiming port ${config.dashboardApiPort} and restarting in ${delayMs}ms...`));
    setTimeout(async () => {
      if (!supervising || shuttingDown) return;
      killPort(config.dashboardApiPort);
      try { await waitForPortFree(config.dashboardApiPort, 3000); } catch { /* proceed */ }
      apiChild = startApi();
      try {
        await waitForHealth(config.dashboardApiPort, '/api/health', 15000);
        console.log(chalk.green('✓ API server recovered'));
      } catch (err: any) {
        console.error(chalk.red('[pan dev] API server unhealthy after restart:'), err.message);
        // The child's own close handler reschedules if it actually exited.
      }
    }, delayMs);
  };

  // ── Hot reload (PAN-1662) ─────────────────────────────────────────────────────
  // Rebuild the dashboard server bundle and restart the API child in place, so
  // merged/edited server code goes live WITHOUT tearing down `pan dev`. Triggered
  // by SIGUSR2 — e.g. `pan reload` run against a live dev session, or the flywheel
  // after merging server-code changes. Pairs with the frontend's graceful
  // reconnect (PAN-1580) so the browser tab recovers on its own.
  const rebuildServerBundle = (): Promise<boolean> =>
    new Promise((resolve) => {
      let settled = false;
      // PAN-1664: use the NON-destructive hot build. The normal build:dashboard:server
      // runs build:contracts which `rmrf`s packages/contracts/dist (and tsdown cleans
      // it) — deleting index.mjs out from under the running Vite frontend, which 404s
      // the @overdeck/contracts module and wedges open tabs on "Reconnecting…".
      // build:dashboard:server:hot + PAN_HOT_RELOAD=1 overwrites dist in place instead.
      const build = spawn('npm', ['run', 'build:dashboard:server:hot'], {
        cwd: join(__dirname, '..', '..'),
        stdio: 'pipe',
        env: { ...process.env, SKIP_DOCS_INDEX: '1', PAN_HOT_RELOAD: '1' },
      });
      // PAN-1664: bound the build so a hung rebuild can never leave the promise
      // unresolved (which would wedge the reloadApi single-flight flag forever).
      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        console.error(chalk.red('[pan dev] server rebuild timed out (120s) — aborting reload'));
        try { build.kill('SIGKILL'); } catch { /* ignore */ }
        resolve(false);
      }, 120_000);
      const done = (ok: boolean) => { if (settled) return; settled = true; clearTimeout(timer); resolve(ok); };
      build.stdout?.on('data', (d) => process.stdout.write(chalk.dim(`[reload] ${d}`)));
      build.stderr?.on('data', (d) => process.stderr.write(chalk.dim(`[reload] ${d}`)));
      build.on('error', () => done(false));
      build.on('close', (code) => done(code === 0));
    });

  const reloadApi = async (reason: string): Promise<void> => {
    if (!supervising || shuttingDown) return;
    if (apiReloading) { reloadPending = true; return; }
    apiReloading = true;
    try {
      console.log(chalk.cyan(`[pan dev] ${reason} — rebuilding server bundle…`));
      const ok = await rebuildServerBundle();
      if (!ok) {
        console.error(chalk.red('[pan dev] server rebuild FAILED — keeping the current server running. Fix the error, then reload again.'));
        return;
      }
      if (!supervising || shuttingDown) return;
      console.log(chalk.cyan('[pan dev] rebuild OK — restarting API server in place…'));
      intentionalApiKill = true;
      apiChild.kill('SIGTERM');
      try {
        await waitForHealth(config.dashboardApiPort, '/api/health', 15000);
        console.log(chalk.green('✓ Dashboard server reloaded'));
        // PAN-1670: API is healthy again — tell Vite to full-reload open tabs so
        // they re-establish the proxied WS instead of wedging on "Reconnecting…".
        try { writeFileSync(reloadSignalPath, String(Date.now())); } catch { /* best effort */ }
      } catch (err: any) {
        console.error(chalk.red('[pan dev] server unhealthy after reload:'), err.message);
      }
    } finally {
      apiReloading = false;
      if (reloadPending) {
        reloadPending = false;
        void reloadApi('coalesced reload');
      }
    }
  };

  // ── Vite dev server ──────────────────────────────────────────────────────────
  const startVite = (): ChildProcess => {
    const child = spawn('npx', ['vite', '--host', '0.0.0.0', '--port', String(config.dashboardPort)], {
      cwd: frontendDir,
      detached: false,
      stdio: 'pipe',
      env: {
        ...process.env,
        TRAEFIK_ENABLED: config.traefikEnabled ? 'true' : 'false',
        PAN_DEV_RELOAD_SIGNAL: reloadSignalPath,
      },
    });
    child.stdout?.on('data', (data) => process.stdout.write(chalk.dim(`[vite] ${data}`)));
    child.stderr?.on('data', (data) => process.stderr.write(chalk.dim(`[vite] ${data}`)));
    child.on('error', (err) => {
      console.error(chalk.red('Failed to start Vite:'), err.message);
      if (!supervising) {
        if (apiChild) apiChild.kill('SIGTERM');
        process.exit(1);
      } else {
        scheduleViteRespawn();
      }
    });
    child.on('close', (code, signal) => {
      console.error(chalk.yellow(`[pan dev] Vite child closed: pid=${child.pid} code=${code} signal=${signal ?? 'none'} shuttingDown=${shuttingDown}`));
      if (!supervising || shuttingDown) return;
      scheduleViteRespawn();
    });
    return child;
  };

  const scheduleViteRespawn = (): void => {
    if (!supervising || shuttingDown) return;
    if (tooManyRestarts(viteRestarts)) {
      console.error(chalk.red('[pan dev] Vite crash-looping (≥5 restarts in 60s) — giving up. Fix the underlying error, then re-run `pan dev`.'));
      void shutdown('crash-loop');
      return;
    }
    const delayMs = Math.min(500 * viteRestarts.length, 5000);
    console.error(chalk.yellow(`[pan dev] Vite died unexpectedly — reclaiming port ${config.dashboardPort} and restarting in ${delayMs}ms...`));
    setTimeout(async () => {
      if (!supervising || shuttingDown) return;
      killPort(config.dashboardPort);
      try { await waitForPortFree(config.dashboardPort, 3000); } catch { /* proceed */ }
      viteChild = startVite();
      try {
        await waitForHttp200(config.dashboardPort, 10000);
        console.log(chalk.green('✓ Vite dev server recovered'));
      } catch (err: any) {
        console.error(chalk.red('[pan dev] Vite unhealthy after restart:'), err.message);
      }
    }, delayMs);
  };

  // ── Initial startup (fail-fast until `supervising` flips on) ──────────────────
  console.log(chalk.dim('Starting API server (Node 22)...'));
  apiChild = startApi();
  try {
    await waitForHealth(config.dashboardApiPort, '/api/health', 15000);
    console.log(chalk.green('✓ API server ready'));
  } catch (err: any) {
    console.error(chalk.red('API server health check failed:'), err.message);
    apiChild.kill('SIGTERM');
    process.exit(1);
  }

  console.log(chalk.dim('Starting Vite dev server...'));
  viteChild = startVite();
  try {
    await waitForHttp200(config.dashboardPort, 10000);
    console.log(chalk.green('✓ Vite dev server ready'));
  } catch (err: any) {
    console.error(chalk.red('Vite frontend did not start:'), err.message);
    apiChild.kill('SIGTERM');
    viteChild.kill('SIGTERM');
    process.exit(1);
  }

  // Both children are healthy: enable self-healing and publish the marker so the
  // rest of the platform knows an interactive dev session owns these ports.
  supervising = true;
  writeDevSupervisorMarker({
    pid: process.pid,
    dashboardPort: config.dashboardPort,
    apiPort: config.dashboardApiPort,
  });

  // ── Sidecars ───────────────────────────────────────────────────────────────
  await startSidecars();

  // ── URLs ───────────────────────────────────────────────────────────────────
  // The dashboard mints its session from a one-time #overdeck_token=<internal
  // token> in the URL hash (consumeDashboardBootstrapToken). Without it the
  // browser can't authenticate and every gated API call 401s, so surface the
  // Frontend URL WITH the token (PAN-1607).
  const internalToken = getInternalTokenSync();
  const authFragment = internalToken ? `#overdeck_token=${encodeURIComponent(internalToken)}` : '';
  const frontendBase = config.traefikEnabled
    ? `https://${config.traefikDomain}`
    : `http://localhost:${config.dashboardPort}`;
  const apiBase = config.traefikEnabled
    ? `https://${config.traefikDomain}/api`
    : `http://localhost:${config.dashboardApiPort}`;
  console.log('');
  console.log(`  Frontend: ${chalk.cyan(`${frontendBase}${authFragment}`)}`);
  console.log(`  API:      ${chalk.cyan(apiBase)}`);
  if (authFragment) {
    console.log(chalk.dim('  ↑ open the Frontend link — it carries a one-time auth token that signs the dashboard in.'));
  }
  console.log(chalk.dim('\nPress Ctrl+C to stop\n'));

  // ── Graceful shutdown ──────────────────────────────────────────────────────
  const shutdown = async (signal: string) => {
    if (shuttingDown) return;
    shuttingDown = true;
    supervising = false;
    clearDevSupervisorMarker();
    console.log(chalk.dim(`\n${signal} received by pan dev (pid=${process.pid} ppid=${process.ppid}), shutting down...`));

    viteChild.kill('SIGTERM');
    apiChild.kill('SIGTERM');

    // Give them a moment to exit gracefully
    await sleep(2000);

    if (!apiChild.killed) apiChild.kill('SIGKILL');
    if (!viteChild.killed) viteChild.kill('SIGKILL');

    // Stop sidecars
    try {
      const { stopSmeeProcessSync } = await import('../../lib/smee.js');
      stopSmeeProcessSync();
    } catch {
      // ignore
    }
    try {
      const { stopSupervisorProcessSync } = await import('../../lib/supervisor.js');
      stopSupervisorProcessSync();
    } catch {
      // ignore
    }
    try {
      const { stopCliproxySync } = await import('../../lib/cliproxy.js');
      stopCliproxySync();
    } catch {
      // ignore
    }

    process.exit(0);
  };

  process.on('SIGINT', () => shutdown('SIGINT'));
  process.on('SIGTERM', () => shutdown('SIGTERM'));
  process.on('SIGHUP', () => shutdown('SIGHUP'));
  // PAN-1662: reload-in-place signal. `pan reload` (and the flywheel) send
  // SIGUSR2 to this supervisor to rebuild the server bundle and hot-restart the
  // API child without dropping the dev session.
  process.on('SIGUSR2', () => { void reloadApi('reload signal (SIGUSR2)'); });

  // Keep the process alive
  await new Promise(() => {});
}
